/**
 * Read-only candidate phone block shown when location='phone_interview'.
 * Pencil icon toggles the form into edit mode (a FormBuilder phone input
 * is rendered by the schema while editing).
 */
import { Flex, Text } from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { Edit05, Phone, Plus } from '@kula-ai/icons-core';

interface CandidatePhoneFieldProps {
  candidateName?: string;
  candidateAvatar?: string;
  phoneNumber?: string;
  onEdit?: () => void;
}

export const CandidatePhoneField = ({
  candidateName,
  candidateAvatar,
  phoneNumber,
  onEdit,
}: CandidatePhoneFieldProps) => {
  const hasNumber = Boolean(phoneNumber);
  return (
    <Flex
      // Bottom-align so the edit / add control lines up with the phone
      // number row, not centered between the name and the number.
      align="flex-end"
      justify="space-between"
      w="100%"
      py="2"
      px="3"
      bg="secondary-25"
      borderRadius="md"
    >
      <Flex align="center" gap="2">
        <Flex flexDir="column" gap="0.5">
          {candidateName && (
            <Flex gap="1">
              <Avatar
                src={candidateAvatar}
                name={candidateName}
                size="sm"
                shape="square"
              />
              <Text fontSize="sm" fontWeight="medium" color="secondary-900">
                {candidateName}&apos;s phone
              </Text>
            </Flex>
          )}
          <Flex align="center" gap="1.5">
            <Phone size={14} color="var(--kula-colors-secondary-500)" />
            <Text
              fontSize="sm"
              color={hasNumber ? 'secondary-700' : 'secondary-500'}
              fontStyle={hasNumber ? 'normal' : 'italic'}
            >
              {hasNumber ? phoneNumber : 'No phone on record'}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      {onEdit &&
        (hasNumber ? (
          <Flex
            as="button"
            type="button"
            align="center"
            justify="center"
            w="7"
            h="7"
            borderRadius="md"
            cursor="pointer"
            onClick={onEdit}
            aria-label="Edit candidate phone"
          >
            <Edit05 size={16} />
          </Flex>
        ) : (
          <Flex
            as="button"
            type="button"
            align="center"
            gap="1"
            px="2"
            py="1"
            borderRadius="md"
            cursor="pointer"
            color="primary-500"
            _hover={{ bg: 'primary-25' }}
            onClick={onEdit}
            aria-label="Add candidate phone"
          >
            <Plus size={14} />
            <Text fontSize="sm" fontWeight="medium">
              Add
            </Text>
          </Flex>
        ))}
    </Flex>
  );
};
